// client/src/pages/admin/AdminLayout.js
import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useContext } from 'react';
import {
  Container, Grid, Paper, List, ListItemButton, ListItemText, ListSubheader, Typography, Divider,
  Select, MenuItem, FormControl, InputLabel, Box, Alert,
} from '@mui/material';
import { AuthContext } from '../../context/AuthContext';
import { useActingStore } from '../../context/ActingStoreContext';

const ADMIN_NAV_ITEMS = [
  { to: '/admin', label: 'Dashboard', end: true },
  { to: '/admin/products', label: 'Manage Products' },
  { to: '/admin/orders', label: 'Manage Orders' },
  { to: '/admin/invoices', label: 'Invoices' },
  { to: '/admin/quotes', label: 'Quotes' },
  { to: '/admin/proformas', label: 'Proforma Invoices' },
  { to: '/admin/auctions', label: 'Manage Auctions' },
  { to: '/admin/return-requests', label: 'Return Requests' },
  { to: '/admin/coupons', label: 'Coupons' },
  { to: '/admin/categories', label: 'Categories' },
  { to: '/admin/banners', label: 'Homepage Banners' },
  { to: '/admin/seller-applications', label: 'Seller Applications' },
  { to: '/admin/users', label: 'Manage Users' },
  { to: '/admin/feature-flags', label: 'Site Features' },
];

const NavItem = ({ to, label, end }) => (
  <ListItemButton
    component={NavLink}
    to={to}
    end={end}
    sx={{
      borderRadius: 1, mb: 0.5,
      '&.active': { bgcolor: 'action.selected', fontWeight: 700 },
    }}
  >
    <ListItemText primary={label} />
  </ListItemButton>
);

const AdminLayout = () => {
  const { user } = useContext(AuthContext);
  const { actingStoreId, setActingStoreId, managedStores } = useActingStore();
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3} lg={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Admin Panel</Typography>

            {isSuperAdmin && (
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="acting-store-label">Managing store</InputLabel>
                  <Select
                    labelId="acting-store-label"
                    label="Managing store"
                    value={actingStoreId || ''}
                    onChange={(e) => setActingStoreId(Number(e.target.value))}
                  >
                    {managedStores.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {managedStores.length === 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>No stores yet — create one below.</Alert>
                )}
              </Box>
            )}

            <List dense>
              {ADMIN_NAV_ITEMS.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </List>

            {isSuperAdmin && (
              <>
                <Divider sx={{ my: 1 }} />
                <List subheader={<ListSubheader disableSticky sx={{ color: 'error.main' }}>Platform</ListSubheader>} dense>
                  <NavItem to="/admin/stores" label="Manage Stores" />
                </List>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={9} lg={10}>
          <Paper sx={{ p: 3, minHeight: 400 }}>
            {isSuperAdmin && !actingStoreId ? (
              <Alert severity="warning">Select or create a store to manage first.</Alert>
            ) : (
              <Outlet />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminLayout;
